import React, { useEffect, useRef, useState } from "react";
import { Alert, Box, Button, CircularProgress, IconButton, Paper, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import SaveIcon from "@mui/icons-material/Save";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { useCreateUploadURLMutation, useDeleteImageKeyMutation, useGetWorkshopImageUrlQuery, useUpdateImageKeyMutation, useUploadFileMutation } from "../../../app/store/api/workshopApi";
import { useSelector } from "react-redux";
import { selectWorkshopDetails } from "../../../app/store/userSlice";
import { toast } from "react-hot-toast";

const ASPECT = 4 / 3;
const PPI = 72;
const JPEG_QUALITY = 0.9;
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const VARIANTS = [
  { key: "thumbnail", width: 320, height: 240, previewWidth: 160 },
  { key: "medium", width: 800, height: 600, previewWidth: 240 },
  { key: "main", width: 1600, height: 1200, previewWidth: 320 },
];
const ORIGINAL_KEY = "original";
const ALL_VARIANT_KEYS = [...VARIANTS.map((v) => v.key), ORIGINAL_KEY];

const getSourceRect = (img, crop) => ({
  sx: (crop.x / 100) * img.naturalWidth,
  sy: (crop.y / 100) * img.naturalHeight,
  sw: (crop.width / 100) * img.naturalWidth,
  sh: (crop.height / 100) * img.naturalHeight,
});

const drawCrop = (canvas, img, crop, outW, outH) => {
  const { sx, sy, sw, sh } = getSourceRect(img, crop);
  canvas.width = outW;
  canvas.height = outH;
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.clearRect(0, 0, outW, outH);
  ctx.drawImage(img, sx, sy, sw, sh, 0, 0, outW, outH);
};

const canvasToBlob = (canvas, type, quality) => new Promise((resolve, reject) => canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Canvas export failed"))), type, quality));

const setJpegDpi = async (blob, dpi) => {
  const buf = new Uint8Array(await blob.arrayBuffer());
  const isJfif = buf[0] === 0xff && buf[1] === 0xd8 && buf[2] === 0xff && buf[3] === 0xe0 && buf[6] === 0x4a && buf[7] === 0x46 && buf[8] === 0x49 && buf[9] === 0x46;
  if (!isJfif) return blob;
  buf[13] = 1;
  buf[14] = dpi >> 8;
  buf[15] = dpi & 0xff;
  buf[16] = dpi >> 8;
  buf[17] = dpi & 0xff;
  return new Blob([buf], { type: "image/jpeg" });
};

const buildVariantBlob = async (img, crop, { width, height }) => {
  const canvas = document.createElement("canvas");
  drawCrop(canvas, img, crop, width, height);
  const blob = await canvasToBlob(canvas, "image/jpeg", JPEG_QUALITY);
  return setJpegDpi(blob, PPI);
};

const PreviewCanvas = ({ img, crop, width }) => {
  const canvasRef = useRef(null);
  const height = Math.round(width / ASPECT);

  useEffect(() => {
    if (!img || !crop?.width || !canvasRef.current) return;
    drawCrop(canvasRef.current, img, crop, width * 2, height * 2);
  }, [img, crop, width, height]);

  return <canvas ref={canvasRef} style={{ width, height, display: "block", borderRadius: 8, border: "1px solid #e3e6ec", backgroundColor: "#f7f8fa" }} />;
};

const WorkshopImage = () => {
  const { t } = useTranslation();
  const inputRef = useRef(null);
  const workshopDetails = useSelector(selectWorkshopDetails);

  const [draft, setDraft] = useState(null);
  const [imgEl, setImgEl] = useState(null);
  const [crop, setCrop] = useState(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isRemoving, setIsRemoving] = useState(false);

  const [createUploadUrl] = useCreateUploadURLMutation();
  const [uploadFile] = useUploadFileMutation();
  const [updateImageKey] = useUpdateImageKeyMutation();
  const [deleteImageKey] = useDeleteImageKeyMutation();
  const { data, refetch } = useGetWorkshopImageUrlQuery({ workshopId: workshopDetails.id, variant: "main" }, { skip: !workshopDetails.id });

  useEffect(() => {
    return () => {
      if (draft?.url) URL.revokeObjectURL(draft.url);
    };
  }, [draft?.url]);

  const openFilePicker = () => inputRef.current?.click();

  const resetDraft = () => {
    setDraft(null);
    setImgEl(null);
    setCrop(null);
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      toast.error(t("workshopImage.invalidType", "Please select an image file."));
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      toast.error(t("workshopImage.tooLarge", "Image is too large (max 10 MB)."));
      return;
    }

    setImgEl(null);
    setCrop(null);
    setDraft({ file, url: URL.createObjectURL(file) });
  };

  const handleImageLoad = (e) => {
    const img = e.currentTarget;
    setImgEl(img);
    setCrop(centerCrop(makeAspectCrop({ unit: "%", width: 90 }, ASPECT, img.width, img.height), img.width, img.height));
  };

  const handleRemoveSaved = async () => {
    setIsRemoving(true);
    try {
      await Promise.all(ALL_VARIANT_KEYS.map((variant) => deleteImageKey({ workshopId: workshopDetails.id, variant }).unwrap()));
      await refetch();
    } catch (error) {
      toast.error(t("workshopImage.imageRemoveError", "Could not remove the image."));
    } finally {
      setIsRemoving(false);
    }
  };

  const uploadVariant = async (variant, blob) => {
    const { uploadUrl, imageKey } = await createUploadUrl({ workshopId: workshopDetails.id, contentType: blob.type, variant }).unwrap();
    await uploadFile({ uploadUrl, file: blob }).unwrap();
    await updateImageKey({ workshopId: workshopDetails.id, variant, newKey: imageKey }).unwrap();
  };

  const handleSave = async () => {
    if (!draft || !imgEl || !crop?.width) return;
    setIsSaving(true);

    try {
      const croppedBlobs = await Promise.all(VARIANTS.map(async (v) => [v.key, await buildVariantBlob(imgEl, crop, v)]));
      const uploads = [...croppedBlobs, [ORIGINAL_KEY, draft.file]];

      await Promise.all(uploads.map(([variant, blob]) => uploadVariant(variant, blob)));

      toast.success(t("workshopImage.imageUploadSuccess"));
      resetDraft();
      await refetch();
    } catch (error) {
      console.error("Error uploading image:", error);
      toast.error(t("workshopImage.imageUploadError"));
    } finally {
      setIsSaving(false);
    }
  };

  const sourceRect = imgEl && crop?.width ? getSourceRect(imgEl, crop) : null;

  return (
    <Paper variant='outlined' sx={{ borderColor: "#e3e6ec", borderRadius: 2, p: 3, mb: 4 }}>
      <Typography variant='subtitle1' sx={{ fontWeight: 700, color: "#003C59", lineHeight: 1.3 }}>
        {t("workshopImage.title")}
      </Typography>

      <Box sx={{ mb: 2 }}>
        <Typography variant='body2' sx={{ fontWeight: 400, color: "text.primary", lineHeight: 1.3 }}>
          {t("workshopImage.description")}
        </Typography>
      </Box>

      <input ref={inputRef} type='file' accept='image/*' hidden onChange={handleFileChange} />

      {draft ? (
        <>
          <Box 
            sx={{ 
              display: "grid", 
              gap: 3, 
              // gridTemplateColumns: { xs: "1fr", md: "minmax(0, 1.5fr) minmax(0, 1fr)" },
              width: "100%",
              alignItems: "start" 
            }}>
            {/* Left: cropper */}
            <Box>
              <Typography variant='body2' sx={{ color: "text.secondary", mb: 1 }}>
                {t("workshopImage.cropHint", "Drag or resize the box to choose the visible area (4:3).")}
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "center", backgroundColor: "#f7f8fa", border: "1px solid #e3e6ec", borderRadius: 2, p: 1 }}>
                <ReactCrop crop={crop} onChange={(_, percentCrop) => setCrop(percentCrop)} aspect={ASPECT} keepSelection minWidth={40} disabled={isSaving}>
                  <img src={draft.url} alt={draft.file.name} onLoad={handleImageLoad} style={{ maxHeight: 460, maxWidth: "100%", display: "block" }} />
                </ReactCrop>
              </Box>
              {imgEl && (
                <Typography variant='caption' sx={{ color: "text.secondary", display: "block", mt: 1 }}>
                  {t("workshopImage.source", "Source")}: {imgEl.naturalWidth} × {imgEl.naturalHeight}px
                  {sourceRect && ` · ${t("workshopImage.selection", "Selection")}: ${Math.round(sourceRect.sw)} × ${Math.round(sourceRect.sh)}px`}
                </Typography>
              )}
            </Box>

            {/* Right: live preview */}
            {/* <Paper variant='outlined' sx={{ borderColor: "#e3e6ec", borderRadius: 2, p: 2, mt: 3.5 }}>
              <Typography variant='subtitle2' sx={{ fontWeight: 700, color: "#003C59", mb: 1.5 }}>
                {t("workshopImage.preview", "Live preview")}
              </Typography>

              <Stack spacing={2}>
                {VARIANTS.map((v) => {
                  const upscaled = sourceRect && (sourceRect.sw < v.width || sourceRect.sh < v.height);
                  return (
                    <Box key={v.key}>
                      <Stack direction='row' justifyContent='space-between' alignItems='baseline' sx={{ mb: 0.5 }}>
                        <Typography variant='body2' sx={{ fontWeight: 600 }}>
                          {t(`workshopImage.variant.${v.key}`, v.key.charAt(0).toUpperCase() + v.key.slice(1))}
                        </Typography>
                        <Typography variant='caption' sx={{ color: "text.secondary" }}>
                          {v.width} × {v.height}px · {PPI} PPI
                        </Typography>
                      </Stack>
                      <PreviewCanvas img={imgEl} crop={crop} width={v.previewWidth} />
                      {upscaled && (
                        <Typography variant='caption' sx={{ color: "warning.main", display: "block", mt: 0.5 }}>
                          {t("workshopImage.upscaleWarning", "Selection is smaller than this size and will be upscaled.")}
                        </Typography>
                      )}
                    </Box>
                  );
                })}

                <Box>
                  <Stack direction='row' justifyContent='space-between' alignItems='baseline'>
                    <Typography variant='body2' sx={{ fontWeight: 600 }}>
                      {t("workshopImage.variant.original", "Original")}
                    </Typography>
                    <Typography variant='caption' sx={{ color: "text.secondary" }}>
                      {imgEl ? `${imgEl.naturalWidth} × ${imgEl.naturalHeight}px` : "—"} · {t("workshopImage.preserved", "preserved")}
                    </Typography>
                  </Stack>
                </Box>
              </Stack>
            </Paper> */}
          </Box>

          {imgEl && (imgEl.naturalWidth < 1600 || imgEl.naturalHeight < 1200) && (
            <Alert severity='info' sx={{ mt: 2 }}>
              {t("workshopImage.lowResolution", "This image is smaller than 1600 × 1200px, so the Main version will be upscaled.")}
            </Alert>
          )}

          <Stack direction='row' spacing={1.5} sx={{ mt: 2 }}>
            <Button variant='contained' startIcon={isSaving ? <CircularProgress size={16} color='inherit' /> : <SaveIcon fontSize='small' />} onClick={handleSave} disabled={!imgEl || !crop?.width || isSaving} sx={{ bgcolor: "#003C59", "&:hover": { bgcolor: "#003C59" } }}>
              {t("workshopSettings.save")}
            </Button>
            <Button variant='outlined' onClick={resetDraft} disabled={isSaving} sx={{ color: "#003C59", borderColor: "#003C59" }}>
              {t("workshopImage.cancel", "Cancel")}
            </Button>
            <Button onClick={openFilePicker} disabled={isSaving} sx={{ textTransform: "none" }}>
              {t("workshopImage.chooseAnother", "Choose another image")}
            </Button>
          </Stack>
        </>
      ) : data?.imageUrl ? (
        <Stack spacing={2} alignItems='flex-start'>
          <Box
            sx={{
              position: "relative",
              maxWidth: 400,
              borderRadius: 2,
              overflow: "hidden",
              border: "1px solid #e3e6ec",
              backgroundColor: "#f7f8fa",
              "& .remove-overlay": { opacity: 0, pointerEvents: "none" },
              "&:hover .remove-overlay, &:focus-within .remove-overlay": { opacity: 1, pointerEvents: "auto" },
            }}
          >
            <Box component='img' src={data.imageUrl} alt={t("workshopImage.title")} sx={{ width: "100%", maxHeight: 300, objectFit: "cover", display: "block" }} />
            <Box
              className='remove-overlay'
              sx={{
                position: "absolute",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.5,
                backgroundColor: "rgba(0, 0, 0, 0.55)",
                transition: "opacity 0.2s ease",
              }}
            >
              {isRemoving ? (
                <CircularProgress size={24} sx={{ color: "#fff" }} />
              ) : (
                <>
                  <IconButton aria-label={t("workshopImage.remove", "Remove")} onClick={handleRemoveSaved} sx={{ color: "#fff", "&:hover": { color: "#ff6b6b" } }}>
                    <DeleteOutlineIcon fontSize='medium' />
                  </IconButton>
                  <Typography variant='body2' sx={{ color: "#fff", fontWeight: 500 }}>
                    {t("workshopImage.remove", "Remove")}
                  </Typography>
                </>
              )}
            </Box>
          </Box>
          <Button variant='outlined' startIcon={<AddPhotoAlternateOutlinedIcon fontSize='small' />} onClick={openFilePicker} sx={{ textTransform: "none", color: "#003C59", borderColor: "#003C59" }}>
            {t("workshopImage.replace", "Replace image")}
          </Button>
        </Stack>
      ) : (
        <Button
          onClick={openFilePicker}
          fullWidth
          sx={{
            height: 100,
            border: "1px dashed #bbb",
            borderRadius: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            gap: 2,
            color: "text.secondary",
            textTransform: "none",
            "&:hover": { borderColor: "#003C59", backgroundColor: "rgba(0, 60, 89, 0.04)" },
          }}
        >
          <AddPhotoAlternateOutlinedIcon fontSize='medium' />
          <Typography variant='body2'>{t("workshopImage.upload", "Upload")}</Typography>
        </Button>
      )}
    </Paper>
  );
};

export default WorkshopImage;
